import { motion } from "framer-motion";
import { Brain, Check } from "lucide-react";
import { ECOSYSTEM_PRODUCTS } from "@/config/kingbullish";
import { formatCompactUsd } from "@/lib/format";
import { LegendaryBorder, LegendaryCore } from "./LegendaryEffects";
import { LegendaryPower } from "./LegendaryPower";
import { useLegendaryMotion } from "./useLegendaryMotion";
import "@/styles/legendary-10m.css";

const productId = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const ai = ECOSYSTEM_PRODUCTS.find((product) => product.id === "kb-ai");

const LegendaryProducts = ({ products }) => {
  // Keep all existing roadmap product text; include the fourth product explicitly requested.
  const entries = products.some((product) => product.name === ai.name)
    ? products : [...products, { name: ai.name, icon: Brain, items: [] }];
  return (
    <div className="legendary-products" data-testid="roadmap-10m-products">
      {entries.map(({ name, sub, icon: Icon, items }) => {
        const id = `roadmap-10m-product-${productId(name)}`;
        return (
          <div key={name} className="rm-product" data-testid={id}>
            <Icon className="rm-product__icon" aria-hidden="true" />
            <div className="min-w-0">
              <div className="rm-product__name" data-testid={`${id}-name`}>{name}</div>
              {sub && <div className="rm-product__sub" data-testid={`${id}-subtitle`}>{sub}</div>}
              {items.length > 0 && <ul>
                {items.map((item, i) => <li key={item} data-testid={`${id}-detail-${i}`}><Check aria-hidden="true" /> {item}</li>)}
              </ul>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const LegendaryMilestone = ({ cap, title, products, state, children }) => {
  const { ref, beamRef, hovering, burst, running, style, onPointerEnter, onPointerMove, onPointerLeave } = useLegendaryMotion();
  const amount = formatCompactUsd(cap);
  return (
    <motion.div ref={ref} className={`rm-card rm-card--major rm-card--${state} legendary-card`}
      data-testid="roadmap-10m-card" data-hovering={hovering} data-burst={burst} data-motion={running ? "running" : "paused"}
      style={style} onPointerEnter={onPointerEnter} onPointerMove={onPointerMove} onPointerLeave={onPointerLeave}>
      <span className="legendary-chassis" aria-hidden="true" />
      <LegendaryBorder beamRef={beamRef} />
      <LegendaryPower />
      <div className="legendary-badge" data-testid="roadmap-10m-badge">
        <span aria-hidden="true">★</span>
        <span data-testid="roadmap-10m-title">{title}</span>
        <span aria-hidden="true">★</span>
      </div>
      <div className="legendary-heading">
        <LegendaryCore />
        <span className="legendary-crown" aria-hidden="true" data-testid="roadmap-10m-crown">
          <img src="/assets/artwork/legendary/gold-crown.webp" alt="" width="136" height="65" loading="lazy" decoding="async" />
        </span>
        <div className="legendary-amount" data-amount={amount} data-testid="roadmap-10m-cap">
          <span className="legendary-amount__metal">{amount}</span>
          <span className="legendary-amount__sweep" data-amount={amount} aria-hidden="true" />
          <span className="legendary-amount__glint" aria-hidden="true" />
        </div>
      </div>
      <LegendaryProducts products={products} />
      {children}
    </motion.div>
  );
};